// SSE con autenticación por header. El EventSource nativo NO permite headers,
// así que usamos fetch + streaming reader (ver ADR 0010). Reconexión con backoff
// se añade en W1 si hace falta; aquí queda el transporte base.

export interface SseMessage {
  event: string;
  data: string;
  id?: string;
}

export interface SseOptions {
  url: string;
  token?: string | null;
  onMessage: (msg: SseMessage) => void;
  onOpen?: () => void;
  onError?: (err: unknown) => void;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  fetch?: typeof fetch;
}

export interface SseHandle {
  close(): void;
}

function parseFrame(raw: string): SseMessage | null {
  let event = 'message';
  let id: string | undefined;
  const dataLines: string[] = [];
  for (const line of raw.split('\n')) {
    if (!line || line.startsWith(':')) continue;
    const idx = line.indexOf(':');
    const field = idx === -1 ? line : line.slice(0, idx);
    const value = idx === -1 ? '' : line.slice(idx + 1).replace(/^ /, '');
    if (field === 'event') event = value;
    else if (field === 'data') dataLines.push(value);
    else if (field === 'id') id = value;
  }
  if (dataLines.length === 0) return null;
  return { event, data: dataLines.join('\n'), id };
}

export function subscribeSse(opts: SseOptions): SseHandle {
  const controller = new AbortController();
  if (opts.signal) {
    if (opts.signal.aborted) controller.abort();
    else opts.signal.addEventListener('abort', () => controller.abort(), { once: true });
  }
  const doFetch = opts.fetch ?? globalThis.fetch;

  (async () => {
    try {
      const headers = new Headers(opts.headers);
      headers.set('accept', 'text/event-stream');
      if (opts.token) headers.set('authorization', `Bearer ${opts.token}`);

      const res = await doFetch(opts.url, { headers, signal: controller.signal });
      if (!res.ok || !res.body) throw new Error(`SSE HTTP ${res.status}`);
      opts.onOpen?.();

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        let sep: number;
        // Frames separados por línea en blanco (\n\n). Toleramos \r\n\r\n.
        while ((sep = buf.search(/\r?\n\r?\n/)) !== -1) {
          const rawLen = buf.slice(sep).match(/^\r?\n\r?\n/)![0].length;
          const frame = buf.slice(0, sep);
          buf = buf.slice(sep + rawLen);
          const msg = parseFrame(frame);
          if (msg) opts.onMessage(msg);
        }
      }
    } catch (err) {
      if (!controller.signal.aborted) opts.onError?.(err);
    }
  })();

  return { close: () => controller.abort() };
}
